// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase } from './apiBase';

export class ApiAdmin extends ApiBase {
  // ----------------------------
  // Admin Dashboard
  // ----------------------------

  async adminGetDashboard(days: number = 1) {
    return this.client.get('/v1/admin/dashboard', { params: { days } });
  }

  async adminListUsers(params?: {
    limit?: number;
    offset?: number;
    role?: string;
    is_active?: boolean;
    search?: string;
  }) {
    return this.client.get('/v1/admin/users', { params });
  }

  async adminCreateUser(data: {
    email: string;
    password: string;
    name: string;
    role?: string;
    rate_limit_rpm?: number | null;
    max_concurrent_sessions?: number | null;
  }) {
    return this.client.post('/v1/admin/users', data);
  }

  async adminGetUser(userId: string) {
    return this.client.get(`/v1/admin/users/${userId}`);
  }

  // 访问链接（比赛评委直达登录）：网址即钥匙，/login?tk=<token> 打开即登录，
  // 链接内不含账号与明文密码；TTL 内可重复使用，可随时吊销。
  async adminCreateAccessLink(userId: string, ttlHours: number) {
    return this.client.post('/v1/auth/access-links', {
      user_id: userId,
      ttl_hours: ttlHours,
    });
  }

  async adminRevokeAccessLink(token: string) {
    return this.client.delete(`/v1/auth/access-links/${token}`);
  }

  async adminUpdateUser(
    userId: string,
    data: {
      name?: string;
      role?: string;
      is_active?: boolean;
      rate_limit_rpm?: number | null;
      max_concurrent_sessions?: number | null;
    }
  ) {
    return this.client.patch(`/v1/admin/users/${userId}`, data);
  }

  async adminDeleteUser(userId: string) {
    return this.client.delete(`/v1/admin/users/${userId}`);
  }

  async adminHardDeleteUser(userId: string) {
    return this.client.delete(`/v1/admin/users/${userId}/hard`);
  }

  async adminResetPassword(userId: string, newPassword: string) {
    return this.client.post(`/v1/admin/users/${userId}/reset-password`, {
      new_password: newPassword,
    });
  }

  // ----------------------------
  // Batch user management
  // ----------------------------

  async adminBatchUpdateUsers(data: {
    user_ids: string[];
    name?: string;
    role?: string;
    is_active?: boolean;
    rate_limit_rpm?: number | null;
    max_concurrent_sessions?: number | null;
  }) {
    return this.client.patch('/v1/admin/users/batch', data);
  }

  async adminBatchHardDeleteUsers(userIds: string[]) {
    return this.client.delete('/v1/admin/users/batch/hard', { data: { user_ids: userIds } });
  }

  async adminBatchResetPassword(userIds: string[], newPassword: string) {
    return this.client.post('/v1/admin/users/batch/reset-password', {
      user_ids: userIds,
      new_password: newPassword,
    });
  }

  async adminBatchCreateUsers(
    users: Array<{
      email: string;
      password: string;
      name: string;
      role?: string;
      rate_limit_rpm?: number | null;
      max_concurrent_sessions?: number | null;
    }>
  ) {
    return this.client.post('/v1/admin/users/batch', { users });
  }

  async adminUsageTrend(days: number = 7, userId?: string, granularity: string = 'day') {
    const params: Record<string, unknown> = { days, granularity };
    if (userId) params.user_id = userId;
    return this.client.get('/v1/admin/usage/trend', { params });
  }

  async adminUsageLeaderboard(days: number = 7, limit: number = 20) {
    return this.client.get('/v1/admin/usage/leaderboard', { params: { days, limit } });
  }

  async adminUsageModels(days: number = 7) {
    return this.client.get('/v1/admin/usage/models', { params: { days } });
  }

  async adminUsageByEventType(days: number = 7) {
    return this.client.get('/v1/admin/usage/by-event-type', { params: { days } });
  }

  // P2: AI-1 contract — session/provider/model/cache/cost dimensions.
  // Endpoint may not be merged into the working tree yet; callers should
  // handle HTTP errors gracefully and fall back to empty dimensions.
  async adminUsageDimensions(days: number = 7) {
    return this.client.get('/v1/admin/usage/dimensions', { params: { days } });
  }

  async adminListSessions(params?: { limit?: number; offset?: number; user_id?: string }) {
    return this.client.get('/v1/admin/sessions', { params });
  }

  async adminGetSessionMessages(sessionId: string) {
    return this.client.get(`/v1/admin/sessions/${sessionId}/messages`);
  }

  async adminDeleteSession(sessionId: string) {
    return this.client.delete(`/v1/admin/sessions/${sessionId}`);
  }

  async adminSystemHealth() {
    return this.client.get('/v1/admin/system/health');
  }

  async adminSystemMetrics() {
    return this.client.get('/v1/admin/system/metrics');
  }

  async adminSystemDbPool() {
    return this.client.get('/v1/admin/system/db-pool');
  }

  async adminSystemRateLimits() {
    return this.client.get('/v1/admin/system/rate-limits');
  }

  async adminListAuditLogs(params?: {
    limit?: number;
    offset?: number;
    admin_user_id?: string;
    action?: string;
  }) {
    return this.client.get('/v1/admin/audit-logs', { params });
  }

  async adminGetRateLimitSettings() {
    return this.client.get('/v1/admin/settings/rate-limit');
  }

  async adminUpdateRateLimitSettings(data: {
    requests_per_minute?: number;
    window_seconds?: number;
  }) {
    return this.client.patch('/v1/admin/settings/rate-limit', data);
  }

  async adminGetMaintenanceSettings() {
    return this.client.get('/v1/admin/settings/maintenance');
  }

  async adminUpdateMaintenanceSettings(data: { active?: boolean; message?: string }) {
    return this.client.patch('/v1/admin/settings/maintenance', data);
  }

  async adminGetQuotaEnforcementSettings() {
    return this.client.get('/v1/admin/settings/quota-enforcement');
  }

  async adminUpdateQuotaEnforcementSettings(data: { enabled: boolean }) {
    return this.client.patch('/v1/admin/settings/quota-enforcement', data);
  }

  async adminGetCommerceSettings() {
    return this.client.get('/v1/admin/settings/commerce');
  }

  async adminUpdateCommerceSettings(data: { enabled: boolean }) {
    return this.client.patch('/v1/admin/settings/commerce', data);
  }

  async adminGetSolverModeSettings() {
    return this.client.get('/v1/admin/settings/solver-mode');
  }

  async adminUpdateSolverModeSettings(data: { mode: string }) {
    return this.client.patch('/v1/admin/settings/solver-mode', data);
  }

  async adminGetMemorySettings() {
    return this.client.get('/v1/admin/settings/memory');
  }

  async adminUpdateMemorySettings(data: { user_declared_memory_limit: number }) {
    return this.client.patch('/v1/admin/settings/memory', data);
  }

  async adminGetAuthMethodSettings() {
    return this.client.get('/v1/admin/settings/auth-methods');
  }

  async adminUpdateAuthMethodSettings(data: {
    email_password_auth?: boolean;
    email_registration?: boolean;
    email_verification?: boolean;
    qq_auth?: boolean;
    wechat_auth?: boolean;
    phone_auth?: boolean;
  }) {
    return this.client.patch('/v1/admin/settings/auth-methods', data);
  }

  async adminListPlans(params?: { active_only?: boolean }) {
    return this.client.get('/v1/admin/plans', { params });
  }

  async adminCreatePlan(data: {
    code: string;
    name: string;
    description?: string | null;
    price_monthly?: number;
    price_yearly?: number;
    daily_message_limit?: number;
    max_context_length?: number;
    max_knowledge_bases?: number;
    max_file_size_mb?: number;
    sort_order?: number;
    is_active?: boolean;
  }) {
    return this.client.post('/v1/admin/plans', data);
  }

  async adminUpdatePlan(
    planId: string,
    data: {
      description?: string | null;
      price_monthly?: number;
      price_yearly?: number;
      daily_message_limit?: number;
      max_context_length?: number;
      max_knowledge_bases?: number;
      max_file_size_mb?: number;
      sort_order?: number;
      is_active?: boolean;
    }
  ) {
    return this.client.patch(`/v1/admin/plans/${planId}`, data);
  }

  // Subscription & Order Management
  async adminListSubscriptions(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
  }) {
    return this.client.get('/v1/admin/subscriptions', { params });
  }

  async adminSubscriptionStats() {
    return this.client.get('/v1/admin/subscriptions/stats');
  }

  async adminListOrders(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    search?: string;
  }) {
    return this.client.get('/v1/admin/orders', { params });
  }

  async adminCancelOrder(orderNo: string, data?: { reason?: string }) {
    return this.client.post(`/v1/admin/orders/${orderNo}/cancel`, data ?? {});
  }

  async adminRefundOrder(orderNo: string, data?: { reason?: string }) {
    return this.client.post(`/v1/admin/orders/${orderNo}/refund`, data ?? {});
  }

  async adminDowngradeSubscription(userId: string) {
    return this.client.post(`/v1/admin/subscriptions/${userId}/downgrade`);
  }

  async adminAssignSubscription(
    userId: string,
    data: { plan_code: string; billing_cycle?: 'monthly' | 'yearly' }
  ) {
    return this.client.post(`/v1/admin/subscriptions/${userId}/assign`, data);
  }

  // Per-user feature permission overrides
  async adminGetFeatureOverrides(userId: string) {
    return this.client.get(`/v1/admin/users/${userId}/feature-overrides`);
  }

  async adminSetFeatureOverride(
    userId: string,
    feature: string,
    data: { allowed: boolean; note?: string }
  ) {
    return this.client.put(`/v1/admin/users/${userId}/feature-overrides/${feature}`, data);
  }

  async adminClearFeatureOverride(userId: string, feature: string) {
    return this.client.delete(`/v1/admin/users/${userId}/feature-overrides/${feature}`);
  }

  // ----------------------------
  // Bug Reports (Admin)
  // ----------------------------

  async adminListBugs(params?: {
    status?: string;
    severity?: string;
    is_read?: boolean;
    search?: string;
    user_id?: string;
    limit?: number;
    offset?: number;
  }) {
    return this.client.get('/v1/admin/bugs', { params });
  }

  async adminBugUnreadCount() {
    return this.client.get('/v1/admin/bugs/unread-count');
  }

  async adminGetBug(bugId: string) {
    return this.client.get(`/v1/admin/bugs/${bugId}`);
  }

  async adminUpdateBug(
    bugId: string,
    data: {
      status?: string;
      severity?: string;
      admin_notes?: string;
      is_read?: boolean;
      resolved_by?: string;
    }
  ) {
    return this.client.patch(`/v1/admin/bugs/${bugId}`, data);
  }

  async adminMarkBugRead(bugId: string) {
    return this.client.patch(`/v1/admin/bugs/${bugId}/read`);
  }

  async adminResolveBug(bugId: string, adminNotes?: string) {
    const params: Record<string, string> = {};
    if (adminNotes) params.admin_notes = adminNotes;
    return this.client.patch(`/v1/admin/bugs/${bugId}/resolve`, null, { params });
  }
}
